import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from './api'

export interface AdminUser {
  userId: string
  email: string
  displayName: string
  emailConfirmed: boolean
  hasPassword: boolean
  twoFactorEnabled: boolean
  isAdmin: boolean
  createdAt: string
  lastSignInAt: string | null
  lockedOutUntil: string | null
}

const usersKey = ['admin', 'users'] as const

export function useAdminUsers() {
  return useQuery({
    queryKey: usersKey,
    queryFn: () => apiGet<AdminUser[]>('/api/admin/users'),
  })
}

export function useSendPasswordReset() {
  return useMutation({
    mutationFn: (userId: string) =>
      apiSend<null>(`/api/admin/users/${userId}/password-reset`, 'POST'),
  })
}

export function useAdminConfirmEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => apiSend<null>(`/api/admin/users/${userId}/email/confirm`, 'POST'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  })
}

export function useAdminUnlock() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => apiSend<null>(`/api/admin/users/${userId}/unlock`, 'POST'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  })
}

export function useAdminDisableMfa() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => apiSend<null>(`/api/admin/users/${userId}/mfa/disable`, 'POST'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  })
}

export function formatDateTime(value: string | null): string {
  if (value === null) {
    return '—'
  }

  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}
